import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { API } from "@/services/API";
import { getLoadId } from "@/utils/load";
import { fetchAllOffers, updateOfferStatus } from "@/state/offersSlice";
import { useOffersRefresh } from "@/contexts/OffersRefreshContext";
import { updatePrevOffers } from "@/hooks/useOfferStatusNotifications";

export const useCounterOffer = () => {
  const dispatch = useDispatch();
  const resetOffersInterval = useOffersRefresh();
  const { quotes, bookings } = useSelector((state) => state.offers);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (offer, { endpoint, payload, newStatus, buildToast }, onDone) => {
    if (isLoading) return false;
    setIsLoading(true);
    setError(null);
    const loadId = getLoadId(offer.loadBoardItem.uniqueId);
    try {
      await API.post(endpoint, payload);

      // Optimistic status update (user-initiated: no counter badge increment).
      dispatch(updateOfferStatus({ offerId: offer.id, offerType: "quote", newStatus, incrementCounter: false }));

      // Keep the notification ref in sync so auto-refresh doesn't re-toast.
      const updatedQuotes = (quotes.data || []).map((q) => (q.id === offer.id ? { ...q, status: newStatus } : q));
      updatePrevOffers(updatedQuotes, bookings.data);

      if (resetOffersInterval) resetOffersInterval();
      dispatch(fetchAllOffers({ isAutoRefresh: true }));

      buildToast(loadId);
      if (onDone) onDone();
      return true;
    } catch (err) {
      console.error("Counter offer action failed", err);
      setError(typeof err === "string" ? err : "Action failed.");
      toast.error("Couldn't submit your response to the counter offer. Please try again.", {
        className: "cm-toast-error",
        toasterId: "top-right",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const acceptCounter = (offer, onDone) =>
    run(
      offer,
      {
        endpoint: "/parade/quote/counter/accept",
        payload: { quoteId: offer.id, offerRate: offer.counterRate },
        newStatus: "COUNTER_ACCEPTED",
        buildToast: (loadId) =>
          toast.success(`You have accepted the counter bid for Load ${loadId}`, {
            className: "cm-toast-success",
            toasterId: "top-right",
          }),
      },
      onDone,
    );

  const rejectCounter = (offer, onDone) =>
    run(
      offer,
      {
        endpoint: "/parade/quote/counter/reject",
        payload: { quoteId: offer.id },
        newStatus: "COUNTER_REJECTED",
        buildToast: (loadId) =>
          toast.error(`You have declined the counter bid for Load ${loadId}`, {
            className: "cm-toast-error",
            toasterId: "top-right",
          }),
      },
      onDone,
    );

  return { acceptCounter, rejectCounter, isLoading, error };
};
